import type { MouseEvent } from 'react'
import { pulseTooltipOverrides } from '@/shared/components/PulseScope/styled-components'
import pickedDayAsIso from '@/shared/tools/pickedDayAsIso'
import useContractsFilters from '../../ContractsFilters/useContractsFilters'
import { DashboardV2Filters } from '../../types'
import isoDayAsDate from './isoDayAsDate'
import type { StatusDotToken } from './styled-components'

type FilterKey = keyof DashboardV2Filters

interface ChoiceGroupDefinition {
  filter: FilterKey
  labelKey: string
  optionsKey: string
}

const STATUS_DOTS: StatusDotToken[] = [
  'successPulse',
  'missPulseBorder',
  'dangerPulse',
  'borderPulseHover',
  'borderPulseHover',
]

const VERTICAL_CHOICE_GROUPS: Record<string, ChoiceGroupDefinition[]> = {
  energie: [
    { filter: 'energy', labelKey: 'dashboard:filters.by_energy', optionsKey: 'dashboard:filters.energy_options' },
    {
      filter: 'prestationEnergy',
      labelKey: 'dashboard:filters.prestation_type',
      optionsKey: 'dashboard:filters.prestation_energy_options',
    },
    {
      filter: 'contractType',
      labelKey: 'dashboard:filters.contract_type',
      optionsKey: 'dashboard:filters.contract_type_options',
    },
  ],
  telecom: [
    {
      filter: 'productType',
      labelKey: 'dashboard:filters.by_product_type',
      optionsKey: 'dashboard:filters.product_type_telecom_options',
    },
    {
      filter: 'prestation',
      labelKey: 'dashboard:filters.prestation_type',
      optionsKey: 'dashboard:filters.prestation_telecom_options',
    },
  ],
  assurance: [
    {
      filter: 'productTypeInsurance',
      labelKey: 'dashboard:filters.by_product_type',
      optionsKey: 'dashboard:filters.product_type_insurance_options',
    },
    {
      filter: 'prestationInsurance',
      labelKey: 'dashboard:filters.prestation_type',
      optionsKey: 'dashboard:filters.prestation_insurance_options',
    },
  ],
}

const PROVIDER_LABEL_KEYS: Record<string, string> = {
  energie: 'dashboard:filters.by_provider_energie',
  telecom: 'dashboard:filters.by_provider_telecom',
}

const OWNED = 'owned'

const CREATION_DATE_FIELDS: Array<{ key: 'creationFrom' | 'creationTo'; labelKey: string }> = [
  { key: 'creationFrom', labelKey: 'dashboard:filters.creation_date_from' },
  { key: 'creationTo', labelKey: 'dashboard:filters.creation_date_to' },
]

interface ProviderOption {
  id: string | number
  name: string
}

interface UserOption {
  id: string | number
  firstname: string
  name: string
}

interface SelectChange {
  value: Array<{ id?: string | number }>
}

interface UseFiltersPanelParams {
  filters: DashboardV2Filters
  applicationType: string
  providersList: Record<string, unknown>
  usersList: Record<string, unknown>
  activeCount: number
  onReset: () => void
}

const useFiltersPanel = ({
  filters,
  applicationType,
  providersList,
  usersList,
  activeCount,
  onReset,
}: UseFiltersPanelParams) => {
  const { t, dispatchFilters, manageRadioGroup, mapValues } = useContractsFilters({ filters })

  const buildOptions = (filter: FilterKey, optionsKey: string) => {
    const selected = (mapValues(filters[filter], filter, true) as number[] | undefined) ?? []

    return (t(optionsKey, { returnObjects: true }) as string[]).map((label, index) => ({
      label,
      selected: selected.includes(index),
      onClick: (event: MouseEvent) => manageRadioGroup(event, index, filter),
    }))
  }

  const ownedDetails = filters.details ?? []
  const otherDetails = ownedDetails.filter(detail => detail !== OWNED)

  return {
    title: t('dashboard:filters.title'),
    activeCountLabel: t('dashboard:overview.active_filters', { count: activeCount }),
    hasActiveFilters: activeCount > 0,
    resetLabel: t('dashboard:overview.reset'),
    onReset,
    tooltipOverrides: pulseTooltipOverrides,
    mySubscriptions: {
      groupLabel: t('dashboard:filters.by_details'),
      label: t('dashboard:filters.my_subscriptions'),
      checked: ownedDetails.includes(OWNED),
      onChange: (checked: boolean) =>
        dispatchFilters({ ...filters, details: checked ? [...otherDetails, OWNED] : otherDetails }),
    },
    statusLabel: t('dashboard:filters.status'),
    statusOptions: buildOptions('status', 'dashboard:filters.status_options').map((option, index) => ({
      ...option,
      dot: STATUS_DOTS[index],
    })),
    choiceGroups: (VERTICAL_CHOICE_GROUPS[applicationType] ?? []).map(group => ({
      key: group.filter,
      label: t(group.labelKey),
      options: buildOptions(group.filter, group.optionsKey),
    })),
    provider: {
      label: t(PROVIDER_LABEL_KEYS[applicationType] ?? 'dashboard:filters.by_provider_insurance'),
      options: Object.fromEntries(
        Object.values(providersList ?? {}).map(provider => [
          String((provider as ProviderOption).id),
          (provider as ProviderOption).name,
        ]),
      ),
      selectedValue: filters.providerId ?? '',
      onChange: (params: SelectChange) =>
        dispatchFilters({ ...filters, providerId: String(params.value[0]?.id ?? '') }),
    },
    user: {
      label: t('dashboard:filters.by_user'),
      options: Object.fromEntries(
        Object.values(usersList ?? {}).map(user => [
          String((user as UserOption).id),
          `${(user as UserOption).firstname} ${(user as UserOption).name}`,
        ]),
      ),
      selectedValue: filters.userId ?? '',
      onChange: (params: SelectChange) => dispatchFilters({ ...filters, userId: String(params.value[0]?.id ?? '') }),
    },
    selectPlaceholder: t('common:choose'),
    creationDates: CREATION_DATE_FIELDS.map(field => ({
      key: field.key,
      inputId: `overview-filter-${field.key}`,
      label: t(field.labelKey),
      value: isoDayAsDate(filters[field.key]),
      onChange: ({ date }: { date: unknown }) => {
        const isoDay = pickedDayAsIso(date)
        if (isoDay !== (filters[field.key] ?? '')) dispatchFilters({ ...filters, [field.key]: isoDay })
      },
    })),
    datePlaceholder: t('dashboard:filters.date_placeholder'),
  }
}

export default useFiltersPanel
